import { C, getCurrency, themeCSS, glassStyle } from "./theme"
import { useState, useEffect, useRef } from 'react'
import { Icon } from './components/Icon'
import { ACTIVE_CITIES } from './data/constants'



const CITY_PREVIEWS = {
  Dubai: { hood: 'JVC', rent: 5500, grocery: 1600, transport: 1200, savings: 6200 },
  'Abu Dhabi': { hood: 'Al Reem Island', rent: 6500, grocery: 1500, transport: 1100, savings: 5800 },
  Riyadh: { hood: 'Olaya', rent: 4500, grocery: 1200, transport: 800, savings: 5500 },
  Jeddah: { hood: 'Al Rawdah', rent: 3800, grocery: 1100, transport: 850, savings: 4800 },
  Doha: { hood: 'Al Sadd', rent: 6000, grocery: 1800, transport: 1000, savings: 5200 },
  'Kuwait City': { hood: 'Salmiya', rent: 4500, grocery: 1300, transport: 600, savings: 4000 },
  Manama: { hood: 'Seef', rent: 4000, grocery: 1100, transport: 500, savings: 4500 },
  Muscat: { hood: 'Qurum', rent: 3500, grocery: 1000, transport: 500, savings: 3800 },
}

// Only the live markets appear in the hero cycle + selector (see ACTIVE_CITIES).
const HERO_CITIES = ACTIVE_CITIES

const TESTIMONIALS = [
  { name: 'Ahmed K.', city: 'Dubai', text: 'Settle saved me weeks of research. I knew my budget, neighborhood, and commute in minutes.' },
  { name: 'Sarah L.', city: 'Riyadh', text: 'The school finder alone was worth it. Found 3 British curriculum schools near my apartment.' },
  { name: 'James P.', city: 'Abu Dhabi', text: 'I was panicking about my move. Settle showed me I could afford Saadiyat Island — I had no idea.' },
]

const SLIDES = [
  { title: 'Enter 3 fields', subtitle: 'City, salary, room type — instant results.', visual: [{ icon: 'map-pin', label: 'Riyadh', detail: 'Your city' }, { icon: 'wallet', label: 'SAR 15,000', detail: 'Salary' }, { icon: 'home', label: '1 Bedroom', detail: 'Room type' }], accent: C.accent },
  { title: 'See your budget instantly', subtitle: 'Real costs calculated for your salary.', visual: [{ icon: 'home', label: 'Rent', detail: 'SAR 4,500' }, { icon: 'shopping-cart', label: 'Groceries', detail: 'SAR 1,200' }, { icon: 'car', label: 'Transport', detail: 'SAR 800' }], accent: C.gold },
  { title: 'Pick your neighborhood', subtitle: 'Filtered by budget, commute, and work area.', visual: [{ icon: 'check-circle', label: 'Olaya — 82', detail: '15 min' }, { icon: 'check-circle', label: 'Hittin — 76', detail: 'SAR 7K' }, { icon: 'alert-triangle', label: 'Narjis — 48', detail: '45 min' }], accent: '#185FA5' },
  { title: 'Documents sorted', subtitle: 'Visa, GAMCA, attestation — by nationality.', visual: [{ icon: 'file-text', label: 'Visa', detail: '6 steps' }, { icon: 'hospital', label: 'GAMCA', detail: 'wafid.com' }, { icon: 'clipboard', label: 'Attestation', detail: 'Guide' }], accent: C.purple },
  { title: 'Ask AI anything', subtitle: '"Can I afford a car?" — based on YOUR plan.', visual: [{ icon: 'message', label: 'You:', detail: 'Afford a car?' }, { icon: 'sparkles', label: 'AI:', detail: 'Yes — SAR 1.2K' }, { icon: 'car', label: 'Pick:', detail: 'Corolla' }], accent: C.accent },
]

function Counter({ value, suffix, duration = 1500 }) {
  const [count, setCount] = useState(0)
  const [started, setStarted] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting && !started) setStarted(true) }, { threshold: 0.5 })
    if (ref.current) obs.observe(ref.current)
    return () => obs.disconnect()
  }, [started])
  useEffect(() => {
    if (!started) return
    const target = parseInt(value); const step = target / (duration / 16); let cur = 0
    const t = setInterval(() => { cur = Math.min(cur + step, target); setCount(Math.floor(cur)); if (cur >= target) clearInterval(t) }, 16)
    return () => clearInterval(t)
  }, [started, value, duration])
  return <span ref={ref}>{count}{suffix}</span>
}

function ProductWalkthrough() {
  const [slide, setSlide] = useState(0)
  useEffect(() => { const i = setInterval(() => setSlide(s => (s + 1) % SLIDES.length), 3500); return () => clearInterval(i) }, [])
  const cur = SLIDES[slide]
  return (
    <div style={{ ...glassStyle({ strong: true }), borderRadius: 22, overflow: 'hidden' }}>
      <div style={{ padding: '24px 24px 20px', minHeight: 240 }} key={slide}>
        <div style={{ display: 'flex', gap: 6, marginBottom: 18, justifyContent: 'center' }}>
          {SLIDES.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setSlide(i)}
              aria-label={`Go to slide ${i + 1}`}
              aria-current={i === slide}
              style={{
                height: 3, borderRadius: 2, cursor: 'pointer', transition: 'all 0.4s',
                width: i === slide ? 32 : 16, background: i === slide ? cur.accent : C.glassBorder,
                border: 'none', padding: 0, fontFamily: 'inherit',
              }}
            />
          ))}
        </div>
        <div style={{ textAlign: 'center', marginBottom: 18 }}>
          <div style={{ fontWeight: 400, fontSize: 22, color: C.text, marginBottom: 4, fontFamily: C.serif, letterSpacing: -0.3 }}>{cur.title}</div>
          <div style={{ color: C.muted, fontSize: 13, lineHeight: 1.5, maxWidth: 360, margin: '0 auto' }}>{cur.subtitle}</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
          {cur.visual.map((item, i) => (
            <div key={i} style={{ background: C.glass, border: `1px solid ${C.glassBorder}`, backdropFilter: 'blur(14px) saturate(160%)', WebkitBackdropFilter: 'blur(14px) saturate(160%)', borderRadius: 12, padding: '10px', textAlign: 'center', animation: `fadeUp 0.4s ease ${i * 0.08}s both` }}>
              <div style={{ marginBottom: 4, display: 'flex', justifyContent: 'center', color: cur.accent }}>
                <Icon name={item.icon} size={18} />
              </div>
              <div style={{ fontWeight: 700, fontSize: 11, color: C.text }}>{item.label}</div>
              <div style={{ fontSize: 10, color: C.muted, marginTop: 1 }}>{item.detail}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function FeatureDemo({ icon, title, desc, demoItems, color }) {
  return (
    <div className="feature-demo" style={{ ...glassStyle(), borderRadius: 20, padding: 22 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <div style={{ width: 40, height: 40, borderRadius: 12, background: color + '18', border: `1px solid ${color}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', color }}><Icon name={icon} size={20} /></div>
        <div>
          <div style={{ fontWeight: 400, fontSize: 18, color: C.text, fontFamily: C.serif, letterSpacing: -0.2 }}>{title}</div>
          <div style={{ color: C.muted, fontSize: 12 }}>{desc}</div>
        </div>
      </div>
      {demoItems.map((item, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: C.glass, border: `1px solid ${C.glassBorder}`, backdropFilter: 'blur(14px) saturate(160%)', WebkitBackdropFilter: 'blur(14px) saturate(160%)', borderRadius: 10, padding: '8px 12px', marginBottom: 4 }}>
          <span style={{ fontSize: 12, color: C.muted }}>{item.label}</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: item.color || C.text }}>{item.value}</span>
        </div>
      ))}
    </div>
  )
}

export default function LandingPage({ onGetStarted, onNavigate }) {
  const [scrolled, setScrolled] = useState(false)
  const [heroCity, setHeroCity] = useState('Riyadh')
  const [heroSalary, setHeroSalary] = useState('')
  const [userPicked, setUserPicked] = useState(false)

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40)
    window.addEventListener('scroll', h); return () => window.removeEventListener('scroll', h)
  }, [])

  // Auto-cycle city name until user picks one
  useEffect(() => {
    if (userPicked) return
    const interval = setInterval(() => {
      setHeroCity(prev => {
        const idx = HERO_CITIES.indexOf(prev)
        return HERO_CITIES[(idx + 1) % HERO_CITIES.length]
      })
    }, 2200)
    return () => clearInterval(interval)
  }, [userPicked])

  const handleCityChange = (city) => {
    setHeroCity(city)
    setUserPicked(true)
  }

  const cur = getCurrency(heroCity)
  const preview = CITY_PREVIEWS[heroCity] || CITY_PREVIEWS['Riyadh']
  const sal = Number(heroSalary) || 0
  const hasPreview = sal > 0
  const rentEst = hasPreview ? Math.round(sal * 0.3 / 100) * 100 : preview.rent
  const savingsEst = hasPreview ? Math.max(0, sal - rentEst - preview.grocery - preview.transport - 1500) : preview.savings

  return (
    <div style={{
      background: `radial-gradient(1200px 800px at 10% 0%, ${C.accent}14 0%, transparent 60%), radial-gradient(1000px 700px at 90% 10%, ${C.gold}18 0%, transparent 60%), radial-gradient(900px 600px at 50% 100%, ${C.purple}10 0%, transparent 60%), linear-gradient(180deg, ${C.bg} 0%, ${C.bg} 100%)`,
      color: C.text,
      fontFamily: "'Outfit', sans-serif",
      minHeight: '100vh',
      overflowX: 'hidden',
    }}>
      <style>{`
        * { box-sizing: border-box; }
        @keyframes fadeUp { from { opacity:0; transform:translateY(24px); } to { opacity:1; transform:translateY(0); } }
        @keyframes pulse { 0%,100%{opacity:.5} 50%{opacity:1} }
        .btn-primary:hover { transform:translateY(-2px); box-shadow:0 14px 40px rgba(15,110,86,0.35); }
        .btn-primary { transition:all .2s ease; }
        .feature-demo:hover { border-color:${C.accent}55 !important; transform:translateY(-3px); }
        .feature-demo { transition:all .25s ease; }
        @media(max-width:600px) { .features-grid{grid-template-columns:1fr!important;} }
      `}</style>

      {/* NAV */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, padding: '0 24px',
        background: scrolled ? C.chromeBg : 'transparent',
        backdropFilter: scrolled ? 'blur(22px) saturate(170%)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(22px) saturate(170%)' : 'none',
        borderBottom: scrolled ? `1px solid ${C.glassBorder}` : '1px solid transparent',
        transition: 'all .3s',
        height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display:'flex',alignItems:'center',gap:10 }}>
          <div style={{ width:34,height:34,borderRadius:12,background:`linear-gradient(135deg,${C.accent}30,${C.purple}30)`,border:`1px solid ${C.accent}44`,display:'flex',alignItems:'center',justifyContent:'center' }}><svg width="22" height="22" viewBox="0 0 64 64" fill="none"><rect x="4" y="4" width="56" height="56" rx="15" fill="#0F6E56"/><circle cx="32" cy="13" r="5.5" fill="#BA7517"/><circle cx="32" cy="13" r="2.2" fill="#0F6E56"/><path d="M32 18.5C32 18.5 38 25.5 38 31c0 3.8-2.2 6-6 7.8s-7.5 4-7.5 7.7c0 2.2 1.3 3.5 4.5 3.5" stroke="#fff" strokeWidth="4" strokeLinecap="round" fill="none"/><circle cx="31" cy="50.5" r="3.5" fill="#BA7517"/><circle cx="31" cy="50.5" r="1.4" fill="#0F6E56"/></svg></div>
          <span style={{ fontWeight:400,fontSize:26,letterSpacing:-0.8,color:C.accent,fontFamily:C.serif }}>Settle</span>
        </div>
        <div style={{ display:'flex',alignItems:'center',gap:10 }}>
          <button onClick={onGetStarted} style={{ padding:'10px 18px',...glassStyle(),borderRadius:12,color:C.muted,cursor:'pointer',fontSize:14,fontFamily:'inherit',fontWeight:600 }}>Log in</button>
          <button onClick={onGetStarted} className="btn-primary" style={{ padding:'11px 22px',background:`linear-gradient(135deg, ${C.accent} 0%, ${C.accentMid} 100%)`,border:'none',borderRadius:12,color:'#fff',cursor:'pointer',fontSize:14,fontFamily:'inherit',fontWeight:700,boxShadow:'0 10px 28px rgba(15,110,86,0.28)' }}>Get Started</button>
        </div>
      </nav>

      {/* ══════ HERO WITH INLINE TRIAL ══════ */}
      <section style={{ minHeight:'100vh',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:'100px 24px 60px',textAlign:'center',position:'relative' }}>
        <div style={{ position:'absolute',inset:0,background:`radial-gradient(ellipse 80% 50% at 50% 0%,${C.accentLight} 0%,transparent 70%)`,pointerEvents:'none' }} />
        <div style={{ position:'relative',zIndex:1,maxWidth:720,width:'100%' }}>
          <div style={{ animation:'fadeUp .5s ease forwards' }}>
            <div style={{ display:'inline-flex',alignItems:'center',gap:8,...glassStyle(),borderRadius:99,padding:'7px 18px',marginBottom:28 }}>
              <div style={{ width:6,height:6,borderRadius:'50%',background:C.accent,animation:'pulse 2s infinite' }} />
              <span style={{ fontSize:11,color:C.muted,fontWeight:700,letterSpacing:1.3,textTransform:'uppercase' }}>Live in Dubai, Abu Dhabi & Riyadh</span>
            </div>
          </div>

          <h1 style={{ fontFamily:C.serif,fontSize:'clamp(36px,7vw,68px)',fontWeight:400,lineHeight:1.08,letterSpacing:-2,margin:'0 0 20px',animation:'fadeUp .6s ease .1s both' }}>
            <span style={{ color:C.text,fontStyle:'italic' }}>Moving</span>
            <span style={{ color:C.text }}> to </span>
            <span key={heroCity} style={{ background:`linear-gradient(135deg,${C.accent},${C.gold})`,WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent',display:'inline-block',animation:'fadeUp .4s ease both',minWidth:120 }}>{heroCity}?</span>
            <br />
            <span style={{ color:C.text,fontSize:'clamp(24px,4.5vw,44px)' }}>See your budget in 30 seconds.</span>
          </h1>

          <p style={{ fontSize:'clamp(15px,2vw,18px)',color:C.muted,lineHeight:1.7,maxWidth:500,margin:'0 auto 32px',animation:'fadeUp .6s ease .2s both' }}>
            Pick your city and enter your salary — Settle shows your rent range, top neighborhood, and savings potential instantly.
          </p>

          {/* INLINE TRIAL */}
          <div style={{ animation:'fadeUp .6s ease .3s both',maxWidth:520,margin:'0 auto 24px' }}>
            <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:12 }}>
              <select value={heroCity} onChange={e=>handleCityChange(e.target.value)} style={{ padding:'16px',borderRadius:14,...glassStyle(),color:C.text,fontSize:16,fontFamily:'inherit',cursor:'pointer',outline:'none',WebkitAppearance:'none',backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='${encodeURIComponent(C.muted)}' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,backgroundRepeat:'no-repeat',backgroundPosition:'right 16px center',paddingRight:40 }}>
                {HERO_CITIES.map(c=><option key={c} value={c}>{c}</option>)}
              </select>
              <input type="number" value={heroSalary} onChange={e=>setHeroSalary(e.target.value)} placeholder={`Salary in ${cur}`} inputMode="numeric" style={{ padding:'16px',borderRadius:14,...glassStyle(),color:C.text,fontSize:16,fontFamily:'inherit',outline:'none',WebkitAppearance:'none',width:'100%' }} />
            </div>
            <button onClick={onGetStarted} className="btn-primary" style={{ width:'100%',padding:'18px',background:`linear-gradient(135deg, ${C.accent} 0%, ${C.accentMid} 100%)`,border:'none',borderRadius:14,color:'#fff',cursor:'pointer',fontSize:17,fontFamily:'inherit',fontWeight:700,letterSpacing:-0.3,boxShadow:'0 12px 32px rgba(15,110,86,0.32)' }}>
              {hasPreview ? 'See my full plan — Free' : 'Build My Settle Plan — Free'}
            </button>
            <div style={{ color:C.muted,fontSize:12,marginTop:10 }}>No credit card · Free to start · 3 minutes</div>
          </div>

          {/* HR / BUSINESS CTA */}
          <div style={{ animation:'fadeUp .7s ease .35s both',marginTop:8 }}>
            <button onClick={() => onGetStarted && onGetStarted('hr')} style={{
              background:'transparent',border:'none',cursor:'pointer',
              color:C.muted,fontSize:13,fontFamily:'inherit',fontWeight:600,
              display:'inline-flex',alignItems:'center',gap:6,
              padding:'8px 16px',borderRadius:99,transition:'all .2s',
            }}
              onMouseOver={e=>{e.currentTarget.style.color=C.accent;e.currentTarget.style.background=C.accent+'12'}}
              onMouseOut={e=>{e.currentTarget.style.color=C.muted;e.currentTarget.style.background='transparent'}}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4-4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
              Managing a team's relocation? Try Settle for HR →
            </button>
          </div>

          {/* LIVE PREVIEW CARDS */}
          <div style={{ animation:'fadeUp .7s ease .4s both',maxWidth:600,margin:'0 auto' }}>
            <div style={{ display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10 }}>
              {[
                { icon:'home',label:'Suggested rent',value:`${cur} ${rentEst.toLocaleString()}/mo`,color:C.accent },
                { icon:'map-pin',label:'Top neighborhood',value:preview.hood,color:C.purple },
                { icon:'piggy-bank',label:hasPreview?'Est. savings':'Avg. savings',value:`${cur} ${savingsEst.toLocaleString()}/mo`,color:C.gold },
              ].map(card=>(
                <div key={card.label} style={{ ...glassStyle(),borderRadius:18,padding:'16px 14px',transition:'all .3s' }}>
                  <div style={{ marginBottom:8, color:card.color }}><Icon name={card.icon} size={22} /></div>
                  <div style={{ color:C.muted,fontSize:10,fontWeight:700,letterSpacing:1.2,textTransform:'uppercase',marginBottom:4 }}>{card.label}</div>
                  <div style={{ color:card.color,fontWeight:400,fontSize:16,lineHeight:1.3,fontFamily:C.serif,letterSpacing:-0.3 }}>{card.value}</div>
                </div>
              ))}
            </div>
            {hasPreview && (
              <div style={{ textAlign:'center',marginTop:10,fontSize:12,color:C.accent,fontWeight:600 }}>
                Based on {cur} {sal.toLocaleString()}/mo in {heroCity} — sign up for the full breakdown
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ══════ SOCIAL PROOF ══════ */}
      <section style={{ padding:'48px 24px',position:'relative' }}>
        <div style={{ maxWidth:900,margin:'0 auto',...glassStyle({ strong: true }),borderRadius:24,padding:'36px 28px' }}>
          <div style={{ display:'flex',justifyContent:'center',gap:32,flexWrap:'wrap',marginBottom:28 }}>
            {[{value:'3',suffix:'',label:'Cities live'},{value:'20',suffix:'+',label:'Neighborhoods'},{value:'13',suffix:'',label:'Nationalities'},{value:'3',suffix:' min',label:'To your plan'}].map(s=>(
              <div key={s.label} style={{ textAlign:'center',minWidth:80 }}>
                <div style={{ fontFamily:C.serif,fontSize:36,fontWeight:400,color:C.accent,lineHeight:1,letterSpacing:-0.8 }}><Counter value={s.value} suffix={s.suffix} /></div>
                <div style={{ color:C.muted,fontSize:10,marginTop:6,fontWeight:700,letterSpacing:1.2,textTransform:'uppercase' }}>{s.label}</div>
              </div>
            ))}
          </div>
          <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))',gap:12 }}>
            {TESTIMONIALS.map((t,i)=>(
              <div key={i} style={{ background:C.glass,border:`1px solid ${C.glassBorder}`,backdropFilter:'blur(14px) saturate(160%)',WebkitBackdropFilter:'blur(14px) saturate(160%)',borderRadius:16,padding:'16px 18px' }}>
                <div style={{ color:C.text,fontSize:13,lineHeight:1.6,marginBottom:10,fontStyle:'italic',fontFamily:C.serif }}>"{t.text}"</div>
                <div style={{ display:'flex',alignItems:'center',gap:8 }}>
                  <div style={{ width:28,height:28,borderRadius:'50%',background:`linear-gradient(135deg, ${C.accent}22 0%, ${C.accent}12 100%)`,border:`1px solid ${C.accent}33`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700,color:C.accent,fontFamily:C.serif }}>{t.name[0]}</div>
                  <div><div style={{ fontWeight:700,fontSize:12,color:C.text }}>{t.name}</div><div style={{ fontSize:10,color:C.muted,letterSpacing:0.5,textTransform:'uppercase',fontWeight:600 }}>Moved to {t.city}</div></div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ display:'flex',gap:20,justifyContent:'center',marginTop:24,flexWrap:'wrap' }}>
            {[
              { icon:'check-circle', label:'Secure & private' },
              { icon:'globe', label:'3 Gulf cities' },
              { icon:'sparkles', label:'AI-powered' },
              { icon:'id-card', label:'Works on phone' },
            ].map(b=>(
              <div key={b.label} style={{ color:C.muted,fontSize:11,fontWeight:700,display:'inline-flex',alignItems:'center',gap:6,letterSpacing:1.2,textTransform:'uppercase' }}>
                <Icon name={b.icon} size={14} />
                {b.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════ WALKTHROUGH (auto-play, no audio) ══════ */}
      <section style={{ padding:'64px 24px' }}>
        <div style={{ maxWidth:520,margin:'0 auto' }}>
          <div style={{ textAlign:'center',marginBottom:28 }}>
            <div style={{ fontSize:11,color:C.accent,fontWeight:700,letterSpacing:1.5,marginBottom:12,textTransform:'uppercase' }}>How it works</div>
            <h2 style={{ fontFamily:C.serif,fontSize:'clamp(24px,4vw,36px)',fontWeight:400,letterSpacing:-.5,color:C.text }}>From zero to <span style={{ fontStyle:'italic' }}>settled</span> in 5 steps.</h2>
          </div>
          <ProductWalkthrough />
        </div>
      </section>

      {/* ══════ 3 FEATURES WITH LIVE DEMOS ══════ */}
      <section style={{ padding:'64px 24px',position:'relative' }}>
        <div style={{ maxWidth:900,margin:'0 auto' }}>
          <div style={{ textAlign:'center',marginBottom:36 }}>
            <div style={{ fontSize:11,color:C.accent,fontWeight:700,letterSpacing:1.5,marginBottom:12,textTransform:'uppercase' }}>What you get</div>
            <h2 style={{ fontFamily:C.serif,fontSize:'clamp(24px,4vw,36px)',fontWeight:400,letterSpacing:-.5,color:C.text }}>Not a tool. A <span style={{ fontStyle:'italic' }}>co-pilot</span>.</h2>
          </div>
          <div className="features-grid" style={{ display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(270px,1fr))',gap:14 }}>
            <FeatureDemo icon="wallet" title="Budget breakdown" desc="Where your salary goes" color={C.accent} demoItems={[
              {label:'Rent (1BR)',value:'SAR 4,500',color:C.accent},{label:'Groceries',value:'SAR 1,200',color:C.gold},{label:'Transport',value:'SAR 800'},{label:'Remaining',value:'SAR 5,500',color:C.accent},
            ]} />
            <FeatureDemo icon="building" title="Neighborhood finder" desc="Matched to commute + budget" color={C.purple} demoItems={[
              {label:'Olaya',value:'82/100',color:C.accent},{label:'Hittin',value:'76/100',color:C.gold},{label:'Sulaimaniyah',value:'71/100',color:C.gold},{label:'Al Narjis',value:'48/100',color:C.danger},
            ]} />
            <FeatureDemo icon="sparkles" title="AI assistant" desc="Answers based on YOUR plan" color={C.gold} demoItems={[
              {label:'You:',value:'Can I afford a car?'},{label:'Budget left:',value:'SAR 5,500/mo',color:C.accent},{label:'Suggestion:',value:'Corolla — SAR 680',color:C.accent},{label:'Verdict:',value:'Yes, fits budget',color:C.accent},
            ]} />
          </div>
        </div>
      </section>

      {/* ══════ HR / TEAMS SECTION ══════ */}
      <section style={{ padding:'64px 24px' }}>
        <div style={{ maxWidth:640,margin:'0 auto' }}>
          <div style={{ textAlign:'center',marginBottom:28 }}>
            <div style={{ fontSize:11,color:C.purple,fontWeight:700,letterSpacing:1.5,marginBottom:12,textTransform:'uppercase' }}>For HR teams</div>
            <h2 style={{ fontFamily:C.serif,fontSize:'clamp(24px,4vw,36px)',fontWeight:400,letterSpacing:-.5,color:C.text }}>Relocating a team? <span style={{ fontStyle:'italic' }}>Track</span> everyone.</h2>
            <p style={{ color:C.muted,fontSize:15,lineHeight:1.7,maxWidth:460,margin:'12px auto 0' }}>
              One dashboard for your entire team's relocation journey. See who's settled, who needs help, and how your budget is trending.
            </p>
          </div>
          <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:12,marginBottom:28 }}>
            {[
              { icon:'bar-chart',title:'Employee Pipeline',desc:'Kanban board tracks every employee from invite to settled' },
              { icon:'wallet',title:'Cost Analytics',desc:'Aggregate salary, rent, and budget data across your team' },
              { icon:'link',title:'Invite Links',desc:'One-click invite codes — employees join in seconds' },
              { icon:'check-circle',title:'Privacy First',desc:'HR sees progress stages, not personal budget details' },
            ].map(f=>(
              <div key={f.title} style={{ ...glassStyle(),borderRadius:18,padding:'20px 18px' }}>
                <div style={{ width:40,height:40,borderRadius:12,background:`linear-gradient(135deg, ${C.purple}22 0%, ${C.purple}10 100%)`,border:`1px solid ${C.purple}33`,color:C.purple,display:'flex',alignItems:'center',justifyContent:'center',marginBottom:12 }}>
                  <Icon name={f.icon} size={20} />
                </div>
                <div style={{ fontWeight:400,fontSize:16,color:C.text,marginBottom:4,fontFamily:C.serif,letterSpacing:-0.2 }}>{f.title}</div>
                <div style={{ color:C.muted,fontSize:12,lineHeight:1.5 }}>{f.desc}</div>
              </div>
            ))}
          </div>
          <div style={{ textAlign:'center' }}>
            <button onClick={() => onGetStarted && onGetStarted('hr')} className="btn-primary" style={{
              padding:'16px 36px',background:`linear-gradient(135deg, ${C.purple} 0%, #5548B8 100%)`,border:'none',borderRadius:14,
              color:'#fff',cursor:'pointer',fontSize:16,fontFamily:'inherit',fontWeight:700,
              boxShadow:'0 12px 32px rgba(103,89,199,0.32)',
            }}>Set Up HR Dashboard — Free</button>
            <div style={{ color:C.muted,fontSize:12,marginTop:10 }}>Free for teams up to 10 employees</div>
          </div>
        </div>
      </section>

      {/* ══════ FINAL CTA ══════ */}
      <section style={{ padding:'72px 24px 100px' }}>
        <div style={{ maxWidth:520,margin:'0 auto',textAlign:'center' }}>
          <div style={{
            background: `linear-gradient(135deg, ${C.accent} 0%, ${C.accentMid} 100%)`,
            borderRadius: 26,
            padding: '52px 32px',
            color: '#fff',
            boxShadow: '0 20px 56px rgba(15,110,86,0.38)',
          }}>
            <div style={{ marginBottom:18,display:'flex',justifyContent:'center' }}><svg width="48" height="48" viewBox="0 0 64 64" fill="none"><rect x="4" y="4" width="56" height="56" rx="15" fill="#fff"/><circle cx="32" cy="13" r="5.5" fill="#BA7517"/><circle cx="32" cy="13" r="2.2" fill="#fff"/><path d="M32 18.5C32 18.5 38 25.5 38 31c0 3.8-2.2 6-6 7.8s-7.5 4-7.5 7.7c0 2.2 1.3 3.5 4.5 3.5" stroke="#0F6E56" strokeWidth="4" strokeLinecap="round" fill="none"/><circle cx="31" cy="50.5" r="3.5" fill="#BA7517"/><circle cx="31" cy="50.5" r="1.4" fill="#fff"/></svg></div>
            <h2 style={{ fontFamily:C.serif,fontSize:'clamp(26px,4vw,40px)',fontWeight:400,letterSpacing:-1,marginBottom:12,lineHeight:1.1,color:'#fff' }}>Your Gulf move <span style={{ fontStyle:'italic' }}>starts</span> here.</h2>
            <p style={{ color:'rgba(255,255,255,0.85)',fontSize:15,lineHeight:1.7,marginBottom:28,maxWidth:360,margin:'0 auto 28px' }}>Join hundreds of expats who used Settle to plan their relocation — smarter, faster, and stress-free.</p>
            <button onClick={onGetStarted} className="btn-primary" style={{ padding:'17px 42px',background:'#fff',border:'none',borderRadius:14,color:C.accent,cursor:'pointer',fontSize:16,fontFamily:'inherit',fontWeight:700,display:'block',margin:'0 auto',boxShadow:'0 8px 22px rgba(0,0,0,0.18)' }}>Start Free — 3 minutes</button>
            <div style={{ color:'rgba(255,255,255,0.7)',fontSize:12,marginTop:14 }}>No credit card · Works on any device</div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ borderTop:`1px solid ${C.glassBorder}`,padding:'28px 24px',textAlign:'center',background:C.chromeBgSoft,backdropFilter:'blur(18px) saturate(160%)',WebkitBackdropFilter:'blur(18px) saturate(160%)' }}>
        <div style={{ display:'flex',alignItems:'center',justifyContent:'center',gap:10,marginBottom:8 }}>
          <svg width="20" height="20" viewBox="0 0 64 64" fill="none"><rect x="4" y="4" width="56" height="56" rx="15" fill="#0F6E56"/><circle cx="32" cy="13" r="5.5" fill="#BA7517"/><circle cx="32" cy="13" r="2.2" fill="#0F6E56"/><path d="M32 18.5C32 18.5 38 25.5 38 31c0 3.8-2.2 6-6 7.8s-7.5 4-7.5 7.7c0 2.2 1.3 3.5 4.5 3.5" stroke="#fff" strokeWidth="4" strokeLinecap="round" fill="none"/><circle cx="31" cy="50.5" r="3.5" fill="#BA7517"/><circle cx="31" cy="50.5" r="1.4" fill="#0F6E56"/></svg>
          <span style={{ fontWeight:400,fontSize:22,color:C.accent,letterSpacing:-.5,fontFamily:C.serif }}>Settle</span>
        </div>
        <div style={{ color:C.muted,fontSize:12,marginBottom:10 }}>AI-powered Gulf relocation · Dubai · Abu Dhabi · Riyadh</div>
        <div style={{ display:'flex',gap:20,justifyContent:'center',flexWrap:'wrap' }}>
          <span onClick={() => onNavigate && onNavigate('privacy')} style={{ color:C.muted,fontSize:10,cursor:'pointer',fontWeight:700,letterSpacing:1.2,textTransform:'uppercase' }}>Privacy Policy</span>
          <span onClick={() => onNavigate && onNavigate('terms')} style={{ color:C.muted,fontSize:10,cursor:'pointer',fontWeight:700,letterSpacing:1.2,textTransform:'uppercase' }}>Terms of Service</span>
        </div>
      </footer>

      {/* STICKY MOBILE CTA */}
      {scrolled && (
        <div style={{
          position:'fixed',bottom:0,left:0,right:0,zIndex:99,
          background:C.chromeBg,
          backdropFilter:'blur(22px) saturate(170%)',
          WebkitBackdropFilter:'blur(22px) saturate(170%)',
          borderTop:`1px solid ${C.glassBorder}`,
          padding:'10px 16px',paddingBottom:'max(10px,env(safe-area-inset-bottom))',
          display:'flex',alignItems:'center',justifyContent:'space-between',gap:12,
        }}>
          <div style={{ flex:1,minWidth:0 }}>
            <div style={{ fontWeight:400,fontSize:16,color:C.text,fontFamily:C.serif,letterSpacing:-0.2 }}>Plan your Gulf move</div>
            <div style={{ fontSize:10,color:C.muted,letterSpacing:1.2,textTransform:'uppercase',fontWeight:700 }}>Free · 3 min · AI-powered</div>
          </div>
          <button onClick={onGetStarted} style={{ padding:'12px 24px',background:`linear-gradient(135deg, ${C.accent} 0%, ${C.accentMid} 100%)`,border:'none',borderRadius:12,color:'#fff',fontWeight:700,fontSize:14,cursor:'pointer',fontFamily:'inherit',whiteSpace:'nowrap',flexShrink:0,boxShadow:'0 8px 22px rgba(15,110,86,0.32)' }}>Start Free</button>
        </div>
      )}
    </div>
  )
}
